import { useCallback } from 'react';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type InfiniteData,
} from '@tanstack/react-query';
import { getHeadersWithUniqueVisitorId } from '../../Auth';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface ConversationItem {
  conversationId: string;
  preview: string;
  title?: string;
  status?: 'in_progress' | 'completed' | 'error';
  lastUpdated: string;
  /** D-7 — mode the thread was created in. Absent on legacy threads (pre-voice); treat missing as 'text'. */
  mode?: 'text' | 'voice';
}

interface ConversationPage {
  conversations: ConversationItem[];
  nextCursor?: string;
}

// ---------------------------------------------------------------------------
// Query key factory
// ---------------------------------------------------------------------------

const QUERY_KEY_PREFIX = 'conversations';

const conversationKeys = {
  all: [QUERY_KEY_PREFIX] as const,
  list: (searchText?: string) =>
    [QUERY_KEY_PREFIX, searchText] as const,
};

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

const DEFAULT_PAGE_SIZE = 30;

const fetchConversationPage = async (
  apiBaseUrl: string,
  limit: number,
  cursor?: string,
  searchText?: string,
): Promise<ConversationPage> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const params = new URLSearchParams({ limit: String(limit) });
  if (cursor) params.set('cursor', cursor);
  if (searchText?.trim()) params.set('searchText', searchText.trim());

  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversations?${params}`, {
    method: 'GET',
    headers: { 'Content-Type': 'application/json', ...headers },
  });
  if (!response.ok) throw new Error(`Failed to fetch conversations: ${response.status}`);
  return response.json();
};

const deleteConversationApi = async (apiBaseUrl: string, conversationId: string): Promise<void> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversations/${conversationId}`, {
    method: 'DELETE',
    headers: { 'Content-Type': 'application/json', ...headers },
  });
  if (!response.ok) throw new Error('Failed to delete conversation');
};

const renameConversationApi = async (
  apiBaseUrl: string,
  conversationId: string,
  title: string,
): Promise<void> => {
  const headers = await getHeadersWithUniqueVisitorId();
  const response = await fetch(`${apiBaseUrl}/api-rewards/concierge/conversations/${conversationId}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json', ...headers },
    body: JSON.stringify({ title }),
  });
  if (!response.ok) throw new Error('Failed to rename conversation');
};

// ---------------------------------------------------------------------------
// Hook options
// ---------------------------------------------------------------------------

interface UseConversationsOptions {
  apiBaseUrl?: string;
  searchText?: string;
  enabled?: boolean;
  pageSize?: number;
}

const DEFAULT_CONVERSATIONS_API_BASE = '/api-rewards';

// ---------------------------------------------------------------------------
// Main hook
// ---------------------------------------------------------------------------

export function useConversations({
  apiBaseUrl,
  searchText,
  enabled = true,
  pageSize = DEFAULT_PAGE_SIZE,
}: UseConversationsOptions) {
  const queryClient = useQueryClient();
  const resolvedApiBaseUrl = apiBaseUrl || DEFAULT_CONVERSATIONS_API_BASE;
  const queryKey = conversationKeys.list(searchText);

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isError,
  } = useInfiniteQuery<ConversationPage>({
    queryKey,
    queryFn: ({ pageParam }) =>
      fetchConversationPage(resolvedApiBaseUrl, pageSize, pageParam as string | undefined, searchText),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    initialPageParam: undefined as string | undefined,
    enabled,
    staleTime: 30_000,
    gcTime: 10 * 60_000,
    refetchOnMount: 'always',
  });

  const allConversations: ConversationItem[] =
    data?.pages.flatMap((p) => p.conversations) ?? [];

  // ── Delete mutation (optimistic) ──────────────────────────────────────
  const deleteMutation = useMutation({
    mutationFn: async (ids: string[]) => {
      await Promise.all(ids.map((id) => deleteConversationApi(resolvedApiBaseUrl, id)));
    },
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.all });
      const snapshots = new Map<
        readonly string[],
        InfiniteData<ConversationPage> | undefined
      >();

      const idsToRemove = new Set(ids);

      queryClient.setQueriesData<InfiniteData<ConversationPage>>(
        { queryKey: conversationKeys.all },
        (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              conversations: page.conversations.filter(
                (c) => !idsToRemove.has(c.conversationId),
              ),
            })),
          };
        },
      );

      return { snapshots };
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });

  // ── Rename mutation (optimistic) ──────────────────────────────────────
  const renameMutation = useMutation({
    mutationFn: async ({ conversationId, title }: { conversationId: string; title: string }) => {
      await renameConversationApi(resolvedApiBaseUrl, conversationId, title);
    },
    onMutate: async ({ conversationId, title }) => {
      await queryClient.cancelQueries({ queryKey: conversationKeys.all });

      queryClient.setQueriesData<InfiniteData<ConversationPage>>(
        { queryKey: conversationKeys.all },
        (old) => {
          if (!old?.pages) return old;
          return {
            ...old,
            pages: old.pages.map((page) => ({
              ...page,
              conversations: page.conversations.map((c) =>
                c.conversationId === conversationId ? { ...c, title } : c,
              ),
            })),
          };
        },
      );
    },
    onError: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: conversationKeys.all });
    },
  });

  // ── Refresh helper ────────────────────────────────────────────────────
  const refresh = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: conversationKeys.all });
  }, [queryClient]);

  return {
    conversations: allConversations,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
    deleteMutation,
    renameMutation,
    refresh,
    queryKey,
  };
}

// ---------------------------------------------------------------------------
// Standalone refresh helper (for components that don't need the full hook)
// ---------------------------------------------------------------------------

export function useConversationRefresh() {
  const queryClient = useQueryClient();
  return useCallback(() => {
    queryClient.invalidateQueries({ queryKey: conversationKeys.all });
  }, [queryClient]);
}

export { conversationKeys };
